import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { parseDate, simpleDate } from 'lib/date';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { colors } from 'styles/theme/colors';

type Props = {
  children: React.ReactNode;
  sharedRecord: LocationEpisode;
};

const GAP = 10;

export default function LinkedRecordTooltip({ children, sharedRecord }: Props) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();

    setPosition({
      x: trigger.left,
      y: trigger.bottom + GAP,
    });
  }, []);

  const handleMouseEnter = () => {
    setVisible(true);
    requestAnimationFrame(updatePosition);
  };

  const handleScroll = useCallback(() => {
    if (visible) setVisible(false);
  }, [visible]);

  useLayoutEffect(() => {
    window.addEventListener('wheel', handleScroll);
    return () => window.removeEventListener('wheel', handleScroll);
  }, [handleScroll]);

  const config = sharedRecord.owner.client.configForManagedGroupType(sharedRecord.group.groupTypeId);

  const details: { label: string; value: string }[] = [];

  // Authorization: same conditions as the Authorization blurb
  const authorization = sharedRecord.activeAuthorizationReview;
  const planTypeIncluded =
    !config?.utilizationManagement?.planTypeClassification?.length ||
    config?.utilizationManagement?.planTypeClassification?.some(
      (planType) => sharedRecord.planTypeClassification?.id === planType.id
    );
  const showAuthorization = !!config?.utilizationManagement?.enabled && planTypeIncluded;

  if (showAuthorization) {
    // Mirrors Authorization blurb (Authorization.tsx lines 92–108)
    const rehabState = sharedRecord.currentRehabState;
    const parsedApprovedThroughDate = parseDate(authorization?.data.approvedThroughDate);

    const isActive = rehabState.queue && !authorization?.rejected && !authorization?.canceled;
    const isAccepted = !rehabState.queue && authorization?.accepted;
    const isApproved =
      authorization?.data.authorizationNumber && !parsedApprovedThroughDate && (isActive || isAccepted);
    const hasExpiry = !rehabState.queue && authorization?.data.authorizationNumber && parsedApprovedThroughDate;
    const daysRemaining = differenceInCalendarDays(parsedApprovedThroughDate ?? new Date(), new Date());

    const showValidThroughDate = rehabState.queue && authorization?.data.validThroughDate && isApproved;

    let authValue = 'Not Yet Approved';
    if (isApproved) {
      authValue = rehabState.queue ? 'Approved (Pending Admission)' : 'Approved';
    } else if (hasExpiry) {
      authValue = `${Math.max(0, daysRemaining)} Day${daysRemaining === 1 ? '' : 's'} Remaining`;
    }

    details.push({ label: 'Authorization', value: authValue });

    if (showValidThroughDate) {
      details.push({ label: 'Valid Through', value: simpleDate(parseDate(authorization!.data.validThroughDate))! });
    }
  }

  // Utilization Manager: same conditions as PatientStoryPage
  if (config?.utilizationManager && sharedRecord.utilizationManagerUser) {
    details.push({
      label: 'Utilization Manager',
      value: sharedRecord.utilizationManagerUser.fullName,
    });
  }

  // Case Manager: same conditions as PatientStoryPage
  if (config?.caseManager && sharedRecord.caseManagerUser) {
    details.push({
      label: 'Case Manager',
      value: sharedRecord.caseManagerUser.fullName,
    });
  }

  // Referral: only if referral is configured and one exists
  const referral = config?.referral ? (sharedRecord.latestReferralReview as Referral | undefined) : undefined;

  // Projected Discharge
  const projectedDischarge = sharedRecord.projectedDischargeReview;
  const projectedDischargeDate = projectedDischarge?.date;
  const isDischargeOverdue = sharedRecord.dischargeOverdue;

  const ownerTypeLabel = sharedRecord.owner.groupType?.displayName ?? '';

  return (
    <>
      <TriggerWrapper ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={() => setVisible(false)}>
        {children}
      </TriggerWrapper>
      {createPortal(
        <TooltipContainer ref={tooltipRef} style={{ top: position.y, left: position.x }} $visible={visible}>
          <Header>
            <HeaderLeft>
              <PatientName>{sharedRecord.patient.name}</PatientName>
              {sharedRecord.planTypeClassification && <PlanType>{sharedRecord.planTypeClassification.name}</PlanType>}
            </HeaderLeft>
            {sharedRecord.trackLos &&
              (sharedRecord.stayDescriptor === 'Past' || !sharedRecord.currentRehabState.queue) && (
                <LOSBadge>
                  <LOSNumber>{sharedRecord.lengthOfStay}</LOSNumber>
                </LOSBadge>
              )}
          </Header>
          {ownerTypeLabel && (
            <ContentSection>
              <OwnerTypeBadge>{ownerTypeLabel}</OwnerTypeBadge>
            </ContentSection>
          )}
          {(details.length > 0 || referral || projectedDischargeDate) && (
            <>
              <Divider />
              <DetailsSection>
                {details.map((detail) => (
                  <DetailLine key={detail.label}>
                    <DetailLabel>{detail.label}:</DetailLabel> {detail.value}
                  </DetailLine>
                ))}
                {projectedDischargeDate && (
                  <>
                    <DetailLine>
                      <DetailLabel>Projected Discharge:</DetailLabel>{' '}
                      <DischargeDate $overdue={isDischargeOverdue}>{simpleDate(projectedDischargeDate)}</DischargeDate>
                    </DetailLine>
                    {projectedDischarge.planningDischargeTo && (
                      <DetailLine>
                        <DetailLabel>Planning Discharge To:</DetailLabel> {projectedDischarge.planningDischargeTo}
                      </DetailLine>
                    )}
                  </>
                )}
                {referral && (
                  <>
                    <DetailLine>
                      <DetailLabel>Referral Status:</DetailLabel>{' '}
                      <ReferralStatus $status={referral.status}>{referral.statusLabel}</ReferralStatus>
                    </DetailLine>
                    {referral.group && (
                      <DetailLine>
                        <DetailLabel>Referred To:</DetailLabel> {referral.group.name}
                        {referral.group.groupType?.displayName && ` (${referral.group.groupType.displayName})`}
                      </DetailLine>
                    )}
                  </>
                )}
              </DetailsSection>
            </>
          )}
        </TooltipContainer>,
        document.body
      )}
    </>
  );
}

const TriggerWrapper = styled.div`
  display: flex;
`;

const TooltipContainer = styled.div<{ $visible: boolean }>`
  position: absolute;
  z-index: 0;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  box-shadow: 0 4px 12px ${colors.boxShadow};
  padding: 0;
  min-width: 200px;
  max-width: 340px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: none;
  transition: opacity 0.15s ease;
`;

const Header = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const PatientName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black100};
`;

const PlanType = styled.div`
  font-size: 10px;
  color: ${colors.black50};
`;

const LOSBadge = styled.div`
  border-radius: 50%;
  height: 20px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${colors.black5};
`;

const LOSNumber = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: ${colors.black100};
`;

const ContentSection = styled.div`
  padding: 0 24px 16px;
`;

const OwnerTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background: ${colors.black5};
  font-size: 12px;
  color: ${colors.black100};
`;

const Divider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${colors.black10};
`;

const DetailsSection = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DetailLine = styled.div`
  font-size: 12px;
  color: ${colors.black100};
`;

const DetailLabel = styled.span`
  color: ${colors.black50};
`;

const referralStatusColors: Record<string, string> = {
  [ReviewStatus.ACCEPTED]: colors.green100,
  [ReviewStatus.REJECTED]: colors.red100,
  [ReviewStatus.CANCELED]: colors.black75,
  [ReviewStatus.PENDING]: colors.black75,
};

const DischargeDate = styled.span<{ $overdue: boolean }>`
  font-weight: 500;
  color: ${({ $overdue }) => ($overdue ? colors.red100 : colors.black100)};
`;

const ReferralStatus = styled.span<{ $status: string }>`
  font-weight: 500;
  color: ${({ $status }) => referralStatusColors[$status] ?? colors.black100};
`;
